import type { RequestPolicy } from 'urql';

import { useIsLoggedIn } from '@order/AuthMachine';
import { DurationUnit, TierStatusName } from '@order/graphql';
import { DateDurationUnit, useLocalizationContext } from '@order/Localization';

import { useSweetpassTierQuery } from './graphql/SweetpassTier.generated';
import { getSweetpassTierNameLocalizationId } from './utils';

/**
 * Returns the sweetpass tier information.
 * This will create a loyalty profile if it doesn't already exists.
 * Due to complications with Postgres, we would like to avoid over-fetching this for new accounts.
 * So most use cases should keep the default request policy of "cache-only".
 * While {refetchSweetpassTier} must be called every time that tier changes:
 * - optIn (changing from opted out to sweetpass)
 * - subscribe (changing from sweetpass to sweetpass+)
 * - cancel free trial (changing from sweetpass+ to sweetpass)
 */
export const useSweetpassTier = (
  requestPolicy: RequestPolicy = 'cache-only',
) => {
  const isLoggedIn = useIsLoggedIn();
  const { t, formatDurationFromUnit } = useLocalizationContext();

  const [sweetpassTierResponse, refetchSweetpassTier] = useSweetpassTierQuery({
    requestPolicy,
    pause: !isLoggedIn,
  });

  const loyaltyProfile = sweetpassTierResponse?.data?.loyaltyProfile;
  const loyaltyId = loyaltyProfile?.loyaltyId;
  const joinDate = loyaltyProfile?.joinDate;
  const sweetpassTier = loyaltyProfile?.tier;
  const sweetpassTierId = loyaltyProfile?.tier?.id;
  const sweetpassTierName = sweetpassTier?.name;
  const sweetpassTierStatus = loyaltyProfile?.tierStatus;

  const sweetpassNextTier = loyaltyProfile?.tier?.nextTier;
  const sweetpassNextTierId = sweetpassNextTier?.id;
  const sweetpassNextTierName = sweetpassNextTier?.name;

  const hasTrialMembership =
    sweetpassTierStatus?.name === TierStatusName.FreeTrial;
  const trialEligibility = loyaltyProfile?.trialEligible;
  const duration = trialEligibility?.[0]?.duration;
  const durationUnit = convertDurationUnit(
    trialEligibility?.[0]?.durationUnit ?? DurationUnit.Day,
  );
  const trialDuration = formatDurationFromUnit({ duration, durationUnit });
  const trialDurationSingular = formatDurationFromUnit({
    duration,
    durationUnit,
    singular: true,
  });
  const isEligibleForFreeTrial = Boolean(trialEligibility?.length);

  const formattedSweetpassTierName = t(
    getSweetpassTierNameLocalizationId(sweetpassTierName, hasTrialMembership),
  );

  const isSweetpassTierLoading = sweetpassTierResponse?.fetching;
  const hasUnknownTierError =
    !loyaltyId && Boolean(sweetpassTierResponse?.error);

  return {
    joinDate,
    loyaltyId,
    hasTrialMembership,
    sweetpassTierId,
    sweetpassTierName,
    formattedSweetpassTierName,
    sweetpassNextTierId,
    sweetpassNextTierName,
    isSweetpassTierLoading,
    isEligibleForFreeTrial,
    trialDuration,
    trialDurationSingular,
    hasUnknownTierError,
    refetchSweetpassTier,
  };
};

function convertDurationUnit(durationUnit: DurationUnit): DateDurationUnit {
  if (durationUnit === DurationUnit.Day) return DateDurationUnit.Day;

  if (durationUnit === DurationUnit.Week) return DateDurationUnit.Week;

  if (durationUnit === DurationUnit.Month) return DateDurationUnit.Month;

  return DateDurationUnit.Year;
}
